import {
  InteractionRequiredAuthError,
  type AccountInfo,
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useCallback, useMemo } from "react";

import type { AuthUser } from "../types/AuthUser";
import { loginRequest, tokenRequest } from "./msalConfig";

interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  signIn: (loginHint?: string) => Promise<void>;
  trySilentSignIn: (loginHint?: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

function getLoginId(account: AccountInfo): string {
  return account.username || account.name || account.localAccountId;
}

function getEmail(account: AccountInfo): string {
  const claims = account.idTokenClaims as
    | {
        email?: string;
        preferred_username?: string;
        upn?: string;
      }
    | undefined;

  return (
    claims?.email ??
    claims?.preferred_username ??
    claims?.upn ??
    account.username ??
    ""
  );
}

function mapAccountToUser(account: AccountInfo | null): AuthUser | null {
  if (!account) {
    return null;
  }

  return {
    loginId: getLoginId(account),
    displayName: account.name ?? account.username ?? "",
    email: getEmail(account),
    identityType: "Entra",
  };
}

export function useAuth(): UseAuthResult {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account =
    instance.getActiveAccount() ??
    (accounts.length === 1 ? accounts[0] : null);

  const user = useMemo(() => mapAccountToUser(account), [account]);

  const signIn = useCallback(async (loginHint?: string): Promise<void> => {
    await instance.loginRedirect({ ...loginRequest, loginHint });
  }, [instance]);

  const trySilentSignIn = useCallback(async (loginHint?: string): Promise<boolean> => {
    try {
      const result = await instance.ssoSilent({ ...loginRequest, loginHint });
      instance.setActiveAccount(result.account);
      return true;
    } catch {
      return false;
    }
  }, [instance]);

  const signOut = useCallback(async (): Promise<void> => {
    await instance.logoutRedirect({
      account: instance.getActiveAccount() ?? undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const activeAccount =
      instance.getActiveAccount() ??
      (accounts.length === 1 ? accounts[0] : null);

    if (!activeAccount) {
      return "";
    }

    try {
      const result = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: activeAccount,
      });

      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({
          ...tokenRequest,
          account: activeAccount,
        });

        throw new Error("Interactive token acquisition was started.", {
            cause: error,
        });
      }

      throw error;
    }
  }, [accounts, instance]);

  return {
    isAuthenticated,
    isLoading: inProgress !== "none",
    user,
    signIn,
    signOut,
    trySilentSignIn,
    getAccessToken,
  };
}